/**
 * Renderer for a single ReviewQueueCard. Same data shape mobile uses
 * (the union of confirm + score cards from
 * firebase/graphql/resolvers/mobile.ts:reviewQueue), so a future
 * migration of ProfileHome's review-inbox section can land on this
 * component without re-deriving the cards from corrective actions.
 *
 * Keeps confirm and score cards visually distinguishable (small
 * top-of-card chip) so the parent knows whether they're approving
 * an AI suggestion (confirm) or rating a watched video (score).
 */
import React from 'react';
import {Link} from 'react-router-dom';
import {Box, Button, Stack, Typography} from '@mui/material';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {ReviewQueueCard} from '../../types/generated/graphql';

export type ReviewQueueAction =
  | 'apply' // confirm: take the AI's suggested action
  | 'dismiss' // confirm: reject the suggestion
  | 'skip' // confirm: snooze for later (server-side skipUntil)
  | 'score_yes' // score: parent agrees video is a good fit
  | 'score_no' // score: parent disagrees
  | 'score_skip'; // score: parent looked, opted not to vote

export interface ReviewQueueItemProps {
  card: ReviewQueueCard;
  pid: string;
  busy?: boolean;
  onAction: (card: ReviewQueueCard, action: ReviewQueueAction) => void;
}

// Severity → leading-dot color. Only relevant for confirm cards;
// score cards use a neutral indigo (the AI hasn't necessarily made
// a high-stakes call yet, the parent's verdict IS the signal).
const SEV_DOT: Record<string, string> = {
  high: '#EF4444',
  optional: '#94A3B8',
};
const SEV_LABEL: Record<string, {label: string; text: string}> = {
  high: {label: 'High priority', text: '#B91C1C'},
  optional: {label: 'Optional', text: '#475569'},
};

export function ReviewQueueItem({
  card,
  pid,
  busy = false,
  onAction,
}: ReviewQueueItemProps): React.JSX.Element {
  const isScore = card.kind === 'score';

  // Title: "Is this a good fit for X?" on score cards (already
  // composed by the resolver via card.question), or the action's
  // own title on confirm cards.
  const title = isScore
    ? card.question ?? 'Is this a good fit?'
    : card.actionTitle ?? 'Suggested action';

  // Body: video-name + channel for score; AI evidence text for confirm.
  const body = isScore
    ? `${card.title ?? 'Untitled video'}${card.channel ? ` · ${card.channel}` : ''}`
    : card.evidence ?? '';

  const sevKey = card.severity === 'high' ? 'high' : 'optional';
  const sev = SEV_LABEL[sevKey];

  const evidenceHref = isScore
    ? card.videoId
      ? `/profile/${pid}/video/${card.videoId}`
      : undefined
    : undefined; // confirm-card nav not provided by the unified shape today

  const handleYes = () => onAction(card, isScore ? 'score_yes' : 'apply');
  const handleNo = () => onAction(card, isScore ? 'score_no' : 'dismiss');
  const handleSkip = () => onAction(card, isScore ? 'score_skip' : 'skip');

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 2,
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        p: 2.5,
        transition: 'border-color 120ms',
        '&:hover': {borderColor: '#CBD5E1'},
      }}
    >
      <CardMedia card={card} />
      <Box sx={{flex: 1, minWidth: 0}}>
        {/* Kind chip — at-a-glance signal of what the parent is being
            asked to do. Score cards get the indigo "Rate" chip; confirm
            cards get the slate "Approve" chip (matches the AI-action
            visual language elsewhere on web). */}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {isScore ? (
            <>
              <VisibilityRoundedIcon sx={{fontSize: 14, color: '#0284C7'}} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#0369A1',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                Rate a watched video
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: SEV_DOT[sevKey],
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: sev.text,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                {sev.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#94A3B8',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  ml: 0.5,
                }}
              >
                · AI suggestion
              </Typography>
            </>
          )}
        </Stack>
        <Typography sx={{fontSize: 15, fontWeight: 700, color: '#0F172A', mt: 1}}>
          {title}
        </Typography>
        {body ? (
          <Typography sx={{fontSize: 13, color: '#475569', mt: 0.75, lineHeight: 1.55}}>
            {body}
          </Typography>
        ) : null}
        {/* Score-card AI report doubles as evidence; confirm cards
            already surface evidence in `body` above. */}
        {isScore && card.aiReport ? (
          <Typography sx={{fontSize: 12, color: '#94A3B8', mt: 1, fontWeight: 500}}>
            GoodShowHQ&apos;s read: {card.aiReport}
          </Typography>
        ) : null}
        <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1.75}} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            onClick={handleYes}
            startIcon={<ThumbUpRoundedIcon sx={{fontSize: 14}} />}
            sx={{
              borderRadius: 999,
              bgcolor: '#10B981',
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              px: 2,
              boxShadow: 'none',
              '&:hover': {bgcolor: '#059669', boxShadow: 'none'},
            }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={handleNo}
            startIcon={<ThumbDownRoundedIcon sx={{fontSize: 14}} />}
            sx={{
              borderRadius: 999,
              borderColor: '#FECACA',
              color: '#B91C1C',
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              px: 2,
              '&:hover': {borderColor: '#FCA5A5', bgcolor: '#FEF2F2'},
            }}
          >
            No
          </Button>
          <Button
            variant="text"
            size="small"
            disabled={busy}
            onClick={handleSkip}
            sx={{
              borderRadius: 999,
              color: '#64748B',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              '&:hover': {bgcolor: '#F1F5F9'},
            }}
          >
            Skip
          </Button>
          {evidenceHref ? (
            <Typography
              component={Link}
              to={evidenceHref}
              sx={{
                ml: 0.5,
                fontSize: 12,
                color: '#3448F0',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {textDecoration: 'underline'},
              }}
            >
              See why ›
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

function CardMedia({card}: {card: ReviewQueueCard}): React.JSX.Element {
  // imageKind is server-supplied. 'channel' → round avatar; 'video' →
  // 16:9 thumbnail. Falls back to 'video' for older cached cards.
  const isChannel = card.imageKind === 'channel';
  if (isChannel) {
    if (card.thumbnailUrl) {
      return (
        <Box
          component="img"
          src={card.thumbnailUrl}
          alt={card.actionTitle ?? card.title ?? ''}
          loading="lazy"
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block',
            mt: 0.25,
          }}
        />
      );
    }
    return (
      <Box
        aria-hidden
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: '#E2E8F0',
          color: '#64748B',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 0.25,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{fontSize: 20}} />
      </Box>
    );
  }
  // Video thumbnail variant
  if (card.thumbnailUrl) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: 96,
          height: 56,
          flexShrink: 0,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 'inset 0 0 0 1px #E2E8F0',
          bgcolor: '#F1F5F9',
          mt: 0.25,
        }}
      >
        <Box
          component="img"
          src={card.thumbnailUrl}
          alt=""
          loading="lazy"
          sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
        />
      </Box>
    );
  }
  return (
    <Box
      aria-hidden
      sx={{
        width: 96,
        height: 56,
        flexShrink: 0,
        borderRadius: 2,
        bgcolor: '#E2E8F0',
        color: '#64748B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mt: 0.25,
      }}
    >
      <PlayCircleOutlineRoundedIcon sx={{fontSize: 22}} />
    </Box>
  );
}
